import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider';
import { hasPermission, PERMISSIONS } from '../../auth/permissions';
import { styles, colors, radius } from '../../styles/theme';
import useCachedQuery, { invalidateQueries } from '../../hooks/useCachedQuery';
import useDebouncedValue from '../../hooks/useDebouncedValue';
import useUrlState from '../../hooks/useUrlState';
import { useToast } from '../../components/Toast';
import { logError } from '../../utils/logError';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

/**
 * Reçete Listesi Sayfası
 * - VIEW_PAGE_RECIPES yetkisi: listeyi görebilir
 * - MANAGE_RECIPES yetkisi: düzenle, kopyala, aktifleştir, arşivle butonları
 */
const ReceteListePage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const toast = useToast();
  // Filtre/arama URL'e bağlı: detaydan geri dönünce korunur, menüden açınca temiz.
  const [tipFiltre, setTipFiltre] = useUrlState('tip', '');
  const [durumFiltre, setDurumFiltre] = useUrlState('durum', '');
  const [markaFiltre, setMarkaFiltre] = useUrlState('marka_id', '');
  const [kanalFiltre, setKanalFiltre] = useUrlState('kanal', '');
  const [kanalTipFiltre, setKanalTipFiltre] = useUrlState('kanal_tip', '');
  const [arama, setArama] = useUrlState('arama', '', { debounceMs: 350 });
  const debouncedArama = useDebouncedValue(arama, 350);
  const userCacheKey = user?.id || user?.email || user?.name || 'anon';

  const canManage = user && hasPermission(user.role, PERMISSIONS.MANAGE_RECIPES);

  // İşletme (marka) dropdown'ı — DB'den (hardcode yasak, SaaS anayasası M.1)
  const [markalar, setMarkalar] = useState([]);
  useEffect(() => {
    fetch(`${API_URL}/api/markalar`, { headers: getAuthHeaders() })
      .then(r => (r.ok ? r.json() : []))
      .then(d => setMarkalar(Array.isArray(d) ? d : []))
      .catch(() => setMarkalar([]));
  }, []);

  // Satış kanalı dropdown'ı — DB'den (hardcode yasak, SaaS anayasası M.1)
  const [kanallar, setKanallar] = useState([]);
  useEffect(() => {
    fetch(`${API_URL}/api/satis-kanallari`, { headers: getAuthHeaders() })
      .then(r => (r.ok ? r.json() : []))
      .then(d => setKanallar(Array.isArray(d) ? d : []))
      .catch(() => setKanallar([]));
  }, []);

  const fetchReceteler = useCallback(async () => {
    const params = new URLSearchParams();
    if (tipFiltre) params.append('tip', tipFiltre);
    if (durumFiltre) params.append('durum', durumFiltre);
    if (markaFiltre) params.append('marka_id', markaFiltre);
    if (kanalFiltre) params.append('kanal_id', kanalFiltre);
    if (kanalTipFiltre) params.append('kanal_tip', kanalTipFiltre);
    if (debouncedArama) params.append('arama', debouncedArama);
    params.append('limit', '200');

    const res = await fetch(`${API_URL}/api/recipes?${params}`, { headers: getAuthHeaders() });
    if (res.status === 401) {
      logout();
      navigate('/dashboard', { replace: true });
      return [];
    }
    if (res.status === 403) {
      toast.error('Bu sayfayi görüntüleme yetkiniz yok');
      return [];
    }
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    return Array.isArray(data) ? data : [];
  }, [tipFiltre, durumFiltre, markaFiltre, kanalFiltre, kanalTipFiltre, debouncedArama, logout, navigate, toast]);

  const {
    data: receteler = [],
    isLoading,
    isValidating,
    mutate,
    revalidate
  } = useCachedQuery(
    ['recipes', userCacheKey, tipFiltre, durumFiltre, markaFiltre, kanalFiltre, kanalTipFiltre, debouncedArama],
    fetchReceteler,
    { staleTime: 15000 }
  );

  const loading = isLoading && receteler.length === 0;

  // Veri geldiğinde, detaydan geri dönüşte scroll pozisyonunu geri getir.

  const handleDuzenle = (id) => {
    if (!canManage) {
      navigate('/recete/duzenle?id=' + id + '&readonly=1');
    } else {
      navigate('/recete/duzenle?id=' + id);
    }
  };

  const handleKopyala = async (id) => {
    const source = receteler.find(r => r.id === id);
    const optimisticId = `temp-copy-${Date.now()}`;
    const previousData = mutate(prev => {
      const list = Array.isArray(prev) ? prev : [];
      if (!source) return list;
      return [{
        ...source,
        id: optimisticId,
        recete_kodu: `${source.recete_kodu}-KOPYA`,
        ad: `${source.ad} (Kopya)`,
        durum: 'TASLAK'
      }, ...list];
    }, { revalidate: false });

    try {
      const res = await fetch(`${API_URL}/api/recipes/${id}/duplicate`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (res.status === 403) {
        mutate(previousData, { revalidate: false });
        toast.error('Reçete kopyalama yetkiniz yok');
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      invalidateQueries(['recete', 'recipe', 'stok', 'dashboard', 'maliyet']);
      await revalidate({ force: true });
      toast.success('Reçete kopyalandi!');
    } catch (err) {
      mutate(previousData, { revalidate: false });
      logError('Kopyalama hatası:', err);
    }
  };

  const handleSil = async (id) => {
    const target = receteler.find(r => r.id === id);
    if (!target) return;
    const onay = window.confirm(
      `"${target.recete_kodu} - ${target.ad}" reçetesini kalıcı olarak silmek istediğinize emin misiniz?\n\nBu işlem geri alınamaz.`
    );
    if (!onay) return;

    const previousData = mutate(prev => {
      const list = Array.isArray(prev) ? prev : [];
      return list.filter(item => item.id !== id);
    }, { revalidate: false });

    try {
      const res = await fetch(`${API_URL}/api/recipes/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (res.status === 403) {
        mutate(previousData, { revalidate: false });
        toast.error('Reçete silme yetkiniz yok');
        return;
      }
      if (res.status === 409) {
        mutate(previousData, { revalidate: false });
        const data = await res.json();
        toast.error(`Silinemedi: ${data.detail}`);
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      invalidateQueries(['recete', 'recipe', 'stok', 'dashboard', 'maliyet']);
      await revalidate({ force: true });
      toast.success('Reçete silindi!');
    } catch (err) {
      mutate(previousData, { revalidate: false });
      logError('Silme hatası:', err);
      toast.error('Reçete silinirken hata oluştu');
    }
  };

  const handleDurumDegistir = async (id, action) => {
    const target = receteler.find(r => r.id === id);
    const optimisticDurum = action === 'activate' ? 'AKTIF' : 'ARSIV';
    const previousData = mutate(prev => {
      const list = Array.isArray(prev) ? prev : [];
      return list.map(item => {
        if (item.id === id) return { ...item, durum: optimisticDurum };
        if (
          action === 'activate' &&
          target?.urun_malzeme_ad &&
          item.id !== id &&
          item.urun_malzeme_ad === target.urun_malzeme_ad &&
          item.durum === 'AKTIF'
        ) {
          return { ...item, durum: 'ARSIV' };
        }
        return item;
      });
    }, { revalidate: false });

    try {
      const res = await fetch(`${API_URL}/api/recipes/${id}/${action}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (res.status === 403) {
        mutate(previousData, { revalidate: false });
        toast.error('Bu islemi yapma yetkiniz yok');
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      invalidateQueries(['recete', 'recipe', 'stok', 'dashboard', 'maliyet']);
      await revalidate({ force: true });
    } catch (err) {
      mutate(previousData, { revalidate: false });
      logError('Durum degisiklik hatası:', err);
    }
  };

  // Istatistikler
  const toplam = receteler.length;
  const yariMamul = receteler.filter(r => r.tip === 'YARI_MAMUL').length;
  const tabak = receteler.filter(r => r.tip === 'TABAK').length;
  const aktif = receteler.filter(r => r.durum === 'AKTIF').length;

  const TipBadge = ({ tip }) => {
    const colors = {
      'YARI_MAMUL': { bg: '#e3f2fd', color: '#1565c0' },
      'TABAK': { bg: '#fce4ec', color: '#c62828' }
    };
    const s = colors[tip] || { bg: '#e9ecef', color: '#495057' };
    return (
      <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: radius.lg, fontSize: '11px', fontWeight: 'bold', background: s.bg, color: s.color }}>
        {tip === 'YARI_MAMUL' ? 'Yari Mamul' : 'Tabak'}
      </span>
    );
  };

  const DurumBadge = ({ durum }) => {
    const colors = {
      'AKTIF': { bg: '#d4edda', color: '#155724' },
      'TASLAK': { bg: '#fff3cd', color: '#856404' },
      'ARSIV': { bg: '#e9ecef', color: '#495057' }
    };
    const s = colors[durum] || { bg: '#e9ecef', color: '#495057' };
    return (
      <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: radius.lg, fontSize: '11px', fontWeight: 'bold', background: s.bg, color: s.color }}>
        {durum}
      </span>
    );
  };

  return (
    <div>
      {/* Header */}
      <div style={{
        background: `linear-gradient(135deg, ${styles.primary}, ${styles.primaryDark})`,
        color: 'white', padding: '15px 25px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center'
      }}>
        <h1 style={{ margin: 0, fontSize: '20px' }}>Reçete Listesi</h1>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          {!canManage && (
            <span style={{ fontSize: '12px', background: 'rgba(255,255,255,0.2)', padding: '4px 12px', borderRadius: radius.lg }}>
              Salt Okunur
            </span>
          )}
          {canManage && (
            <button
              onClick={() => navigate('/recete/olustur')}
              style={{
                background: colors.surface, color: styles.primary, border: 'none',
                padding: '8px 20px', borderRadius: radius.sm, fontWeight: 'bold', cursor: 'pointer'
              }}
            >
              + Yeni Reçete
            </button>
          )}
        </div>
      </div>

      {/* Istatistik Kartlari */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', padding: '15px 20px' }}>
        {[
          { label: 'Toplam', value: toplam, bg: '#e3f2fd', color: '#1565c0' },
          { label: 'Yari Mamul', value: yariMamul, bg: '#e8f5e9', color: '#2e7d32' },
          { label: 'Tabak', value: tabak, bg: '#fce4ec', color: '#c62828' },
          { label: 'Aktif', value: aktif, bg: '#f3e5f5', color: '#6a1b9a' },
        ].map(card => (
          <div key={card.label} style={{
            background: card.bg, padding: '15px', borderRadius: radius.md,
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: card.color }}>{card.value}</div>
            <div style={{ fontSize: '12px', color: card.color, marginTop: '4px' }}>{card.label}</div>
          </div>
        ))}
      </div>

      {/* Filtreler */}
      <div style={{ display: 'flex', gap: '10px', padding: '0 20px 15px', flexWrap: 'wrap' }}>
        <input
          placeholder="Ara — başlık | -soğan ile içerik (malzeme/alt reçete)"
          value={arama}
          onChange={e => setArama(e.target.value)}
          style={{ flex: 1, minWidth: '200px', padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '13px' }}
        />
        <select value={markaFiltre} onChange={e => setMarkaFiltre(e.target.value)}
          style={{ padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '13px' }}>
          <option value="">Tüm İşletmeler</option>
          {markalar.map(m => <option key={m.id} value={m.id}>{m.ad}</option>)}
          <option value="0">Genel (Atanmamış)</option>
        </select>
        <select value={kanalFiltre} onChange={e => setKanalFiltre(e.target.value)}
          style={{ padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '13px' }}>
          <option value="">Tüm Kanallar</option>
          {kanallar.map(k => <option key={k.id} value={k.id}>{k.ad}</option>)}
        </select>
        <select value={kanalTipFiltre} onChange={e => setKanalTipFiltre(e.target.value)}
          style={{ padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '13px' }}>
          <option value="">Tüm Kanal Tipleri</option>
          <option value="ONLINE">Online</option>
          <option value="PLATFORM">Platform</option>
          <option value="FIZIKSEL">Mağaza / Fiziksel</option>
          <option value="DIGER">Diğer</option>
        </select>
        <select value={tipFiltre} onChange={e => setTipFiltre(e.target.value)}
          style={{ padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '13px' }}>
          <option value="">Tüm Tipler</option>
          <option value="YARI_MAMUL">Yari Mamul</option>
          <option value="TABAK">Tabak</option>
        </select>
        <select value={durumFiltre} onChange={e => setDurumFiltre(e.target.value)}
          style={{ padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '13px' }}>
          <option value="">Tüm Durumlar</option>
          <option value="AKTIF">Aktif</option>
          <option value="TASLAK">Taslak</option>
          <option value="ARSIV">Arsiv</option>
        </select>
        {isValidating && !loading && (
          <span style={{ alignSelf: 'center', fontSize: '12px', color: colors.textSecondary, background: '#f1f3f5', borderRadius: radius.lg, padding: '4px 10px' }}>
            Güncelleniyor...
          </span>
        )}
      </div>

      {/* Tablo */}
      <div style={{ padding: '0 20px', overflowX: 'auto' }}>
        {loading ? (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: colors.backgroundAlt, borderBottom: '2px solid #dee2e6' }}>
                <th style={{ padding: '7px', textAlign: 'left' }}>Kod</th>
                <th style={{ padding: '7px', textAlign: 'left' }}>Ad</th>
                <th style={{ padding: '7px', textAlign: 'center' }}>Tip</th>
                <th style={{ padding: '7px', textAlign: 'center' }}>Durum</th>
                <th style={{ padding: '7px', textAlign: 'left' }}>Marka</th>
                <th style={{ padding: '7px', textAlign: 'right' }}>Porsiyon</th>
                <th style={{ padding: '7px', textAlign: 'center' }}>Satir</th>
                <th style={{ padding: '7px', textAlign: 'right' }}>Son Maliyet</th>
                <th style={{ padding: '7px', textAlign: 'right' }}>Satış Fiyatı</th>
                <th style={{ padding: '7px', textAlign: 'center' }}>POS</th>
                <th style={{ padding: '7px', textAlign: 'left' }}>Kanallar</th>
                <th style={{ padding: '7px', textAlign: 'center' }}>Islemler</th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 6 }).map((_, idx) => (
                <tr key={`skel-${idx}`} style={{ borderBottom: `1px solid ${colors.border}` }}>
                  {Array.from({ length: 12 }).map((__, col) => (
                    <td key={`skel-${idx}-${col}`} style={{ padding: '10px' }}>
                      <div style={{ height: '12px', borderRadius: radius.sm, background: '#e9ecef', width: col === 1 ? '85%' : '65%' }} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        ) : receteler.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>Reçete bulunamadı</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: colors.backgroundAlt, borderBottom: '2px solid #dee2e6' }}>
                <th style={{ padding: '7px', textAlign: 'left' }}>Kod</th>
                <th style={{ padding: '7px', textAlign: 'left' }}>Ad</th>
                <th style={{ padding: '7px', textAlign: 'center' }}>Tip</th>
                <th style={{ padding: '7px', textAlign: 'center' }}>Durum</th>
                <th style={{ padding: '7px', textAlign: 'left' }}>Marka</th>
                <th style={{ padding: '7px', textAlign: 'right' }}>Porsiyon</th>
                <th style={{ padding: '7px', textAlign: 'center' }}>Satir</th>
                <th style={{ padding: '7px', textAlign: 'right' }}>Son Maliyet</th>
                <th style={{ padding: '7px', textAlign: 'right' }}>Satış Fiyatı</th>
                <th style={{ padding: '7px', textAlign: 'center' }}>POS</th>
                <th style={{ padding: '7px', textAlign: 'left' }}>Kanallar</th>
                <th style={{ padding: '7px', textAlign: 'center' }}>Islemler</th>
              </tr>
            </thead>
            <tbody>
              {receteler.map(r => (
                <tr key={r.id} style={{ borderBottom: `1px solid ${colors.border}`, cursor: 'pointer' }}
                    onClick={() => handleDuzenle(r.id)}
                    title={canManage ? 'İçeriği düzenle' : 'İçeriği görüntüle'}
                    onMouseEnter={e => e.currentTarget.style.background = '#f8f9fa'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <td style={{ padding: '10px', fontFamily: 'monospace', fontWeight: 'bold', color: styles.primary }}>{r.recete_kodu}</td>
                  <td style={{ padding: '10px' }}>{r.ad}{r.gizli && <span title="Gizli tarif — içerik yetkilendirilmiş kişilere açık" style={{ marginLeft: 6 }}>🔒</span>}</td>
                  <td style={{ padding: '7px', textAlign: 'center' }}><TipBadge tip={r.tip} /></td>
                  <td style={{ padding: '7px', textAlign: 'center' }}><DurumBadge durum={r.durum} /></td>
                  <td style={{ padding: '10px', color: colors.textSecondary, fontSize: '12px' }}>{r.marka_ad || r.marka || 'Genel'}</td>
                  <td style={{ padding: '7px', textAlign: 'right' }}>
                    {r.porsiyon_miktar ? `${r.porsiyon_miktar} ${r.porsiyon_birim}` : '-'}
                  </td>
                  <td style={{ padding: '7px', textAlign: 'center' }}>{r.detail_count}</td>
                  <td style={{ padding: '7px', textAlign: 'right', fontWeight: 'bold' }}>
                    {r.son_maliyet ? `${r.son_maliyet.toFixed(2)} TL` : '-'}
                  </td>
                  <td style={{ padding: '7px', textAlign: 'right' }}>
                    {r.pos_satis_fiyati ? (
                      <span
                        onClick={(e) => { e.stopPropagation(); navigate(`/transfer-fiyat?arama=${encodeURIComponent(r.ad)}`); }}
                        title="Transfer fiyat sayfasına git"
                        style={{ color: styles.primary, cursor: 'pointer', fontWeight: 'bold', textDecoration: 'underline', textDecorationStyle: 'dotted' }}>
                        {r.pos_satis_fiyati.toFixed(2)} TL
                      </span>
                    ) : (
                      <span
                        onClick={(e) => { e.stopPropagation(); navigate(`/transfer-fiyat?arama=${encodeURIComponent(r.ad)}`); }}
                        title="Transfer fiyat sayfasında fiyat gir"
                        style={{ color: colors.textTertiary, cursor: 'pointer', fontSize: '11px', textDecoration: 'underline', textDecorationStyle: 'dotted' }}>
                        Fiyat gir
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '7px', textAlign: 'center' }}>
                    {r.pos_eslesme_sayisi > 0 ? (
                      <span title={(r.pos_eslesmeler || []).join(', ')}
                        style={{ display: 'inline-block', padding: '2px 8px', borderRadius: radius.lg, background: '#e8f5e9', color: '#2e7d32', fontSize: '11px', fontWeight: 'bold' }}>
                        🔗 {r.pos_eslesme_sayisi}
                      </span>
                    ) : (
                      <span style={{ color: colors.textTertiary, fontSize: '12px' }}>-</span>
                    )}
                  </td>
                  <td style={{ padding: '7px' }}>
                    {(r.kanal_kodlari || []).length > 0 ? (
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        {r.kanal_kodlari.map(kod => (
                          <span key={kod}
                            style={{ display: 'inline-block', padding: '2px 8px', borderRadius: radius.lg, background: '#ede7f6', color: '#5e35b1', fontSize: '11px', fontWeight: 'bold' }}>
                            {kod}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span style={{ color: colors.textTertiary, fontSize: '12px' }}>—</span>
                    )}
                  </td>
                  <td style={{ padding: '7px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                    <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                      <button onClick={() => handleDuzenle(r.id)} title={canManage ? 'Düzenle' : 'Görüntüle'}
                        style={{ background: '#e3f2fd', border: 'none', borderRadius: radius.sm, padding: '4px 8px', cursor: 'pointer', fontSize: '12px' }}>
                        {canManage ? 'Düzenle' : 'Görüntüle'}
                      </button>
                      {canManage && (
                        <>
                          <button onClick={() => handleKopyala(r.id)} title="Kopyala"
                            style={{ background: '#fff3e0', border: 'none', borderRadius: radius.sm, padding: '4px 8px', cursor: 'pointer', fontSize: '12px' }}>
                            Kopyala
                          </button>
                          {(r.durum === 'TASLAK' || r.durum === 'ARSIV') && (
                            <button onClick={() => handleDurumDegistir(r.id, 'activate')} title="Aktiflestir"
                              style={{ background: '#e8f5e9', border: 'none', borderRadius: radius.sm, padding: '4px 8px', cursor: 'pointer', fontSize: '12px' }}>
                              Aktif
                            </button>
                          )}
                          {r.durum === 'AKTIF' && (
                            <button onClick={() => handleDurumDegistir(r.id, 'archive')} title="Arşivle"
                              style={{ background: colors.background, border: 'none', borderRadius: radius.sm, padding: '4px 8px', cursor: 'pointer', fontSize: '12px' }}>
                              Arşiv
                            </button>
                          )}
                          {(r.durum === 'TASLAK' || r.durum === 'ARSIV') && (
                            <button onClick={() => handleSil(r.id)} title="Sil"
                              style={{ background: '#ffebee', border: 'none', borderRadius: radius.sm, padding: '4px 8px', cursor: 'pointer', fontSize: '12px', color: '#c62828' }}>
                              Sil
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default ReceteListePage;
